import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useAuthStore } from '../../store/authStore'
import { AlertCircle, CheckCircle, Eye, EyeOff, Check, X } from 'lucide-react'

// Password requirements config
const REQUIREMENTS = [
  { id: 'length',    label: 'At least 12 characters',       test: (p) => p.length >= 12 },
  { id: 'uppercase', label: 'One uppercase letter (A-Z)',    test: (p) => /[A-Z]/.test(p) },
  { id: 'lowercase', label: 'One lowercase letter (a-z)',    test: (p) => /[a-z]/.test(p) },
  { id: 'number',    label: 'One number (0-9)',              test: (p) => /[0-9]/.test(p) },
  { id: 'special',   label: 'One special character (!@#…)', test: (p) => /[!@#$%^&*(),.?":{}|<>]/.test(p) },
]

function getStrength(password) {
  if (!password) return { score: 0, label: '', color: '' }
  const passed = REQUIREMENTS.filter(r => r.test(password)).length
  if (passed <= 1) return { score: 1, label: 'Weak',   color: 'bg-red-500' }
  if (passed <= 3) return { score: 2, label: 'Fair',   color: 'bg-yellow-400' }
  if (passed === 4) return { score: 3, label: 'Good',   color: 'bg-blue-500' }
  return              { score: 4, label: 'Strong', color: 'bg-emerald-500' }
}

export default function Signup() {
  const { signUp, signInWithGoogle } = useAuthStore()
  const [formData, setFormData] = useState({ fullName: '', email: '', password: '', confirmPassword: '' })
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirm, setShowConfirm]   = useState(false)
  const [passwordFocused, setPasswordFocused] = useState(false)
  const [error, setError]   = useState('')
  const [success, setSuccess] = useState(false)
  const [loading, setLoading] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value })

  const strength   = useMemo(() => getStrength(formData.password), [formData.password])
  const allMet     = REQUIREMENTS.every(r => r.test(formData.password))
  const passwordsMatch = formData.confirmPassword && formData.password === formData.confirmPassword

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    if (!allMet) {
      setError('Please make sure your password meets all requirements.')
      return
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match.')
      return
    }

    setLoading(true)
    try {
      await signUp(formData.email, formData.password, { full_name: formData.fullName })
      setSuccess(true)
    } catch (err) {
      setError(err.message || 'Failed to create account')
    } finally {
      setLoading(false)
    }
  }

  const handleGoogleSignUp = async () => {
    setError('')
    setGoogleLoading(true)
    try {
      await signInWithGoogle()
    } catch (err) {
      setError(err.message || 'Failed to sign up with Google')
      setGoogleLoading(false)
    }
  }

  if (success) {
    return (
      <div className="space-y-6 text-center">
        <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900/20 rounded-full flex items-center justify-center mx-auto">
          <CheckCircle className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
        </div>
        <div>
          <h2 className="font-display text-2xl font-bold mb-2">Check your email</h2>
          <p className="text-sm text-muted-light dark:text-muted-dark">
            We sent a verification link to <strong className="text-gray-900 dark:text-white">{formData.email}</strong>
          </p>
        </div>
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4 text-left">
          <p className="text-sm text-blue-800 dark:text-blue-300">
            Click the link in your email to activate your account. Check your spam folder if you don't see it.
          </p>
        </div>
        <Link to="/login" className="block text-sm font-medium text-accent hover:underline">
          Back to login
        </Link>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-3xl font-bold mb-1 text-gray-900 dark:text-white">Create your account</h2>
        <p className="text-sm text-muted-light dark:text-muted-dark">Start streamlining your creative workflow today</p>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-3.5 flex items-start gap-3">
          <AlertCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}

      {/* Google */}
      <button
        onClick={handleGoogleSignUp}
        disabled={googleLoading || loading}
        className="w-full py-3 px-4 bg-white dark:bg-surface-dark text-gray-700 dark:text-white font-medium rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors flex justify-center items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
        type="button"
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
        </svg>
        {googleLoading ? 'Signing up…' : 'Continue with Google'}
      </button>

      <div className="relative flex items-center justify-center">
        <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 border-t border-gray-200 dark:border-gray-700" />
        <span className="relative bg-surface-light dark:bg-surface-dark px-3 text-xs text-gray-400 uppercase tracking-wider">or</span>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Full Name */}
        <div>
          <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            Full Name
          </label>
          <input
            id="fullName" name="fullName" type="text" required
            value={formData.fullName} onChange={handleChange}
            className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-surface-dark text-gray-900 dark:text-white focus:border-accent focus:ring-1 focus:ring-accent outline-none transition-all"
            placeholder="Alex Smith"
          />
        </div>

        {/* Email */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            Work Email
          </label>
          <input
            id="email" name="email" type="email" required
            value={formData.email} onChange={handleChange}
            className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-surface-dark text-gray-900 dark:text-white focus:border-accent focus:ring-1 focus:ring-accent outline-none transition-all"
            placeholder="alex@company.com"
          />
        </div>

        {/* Password */}
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            Password
          </label>
          <div className="relative">
            <input
              id="password" name="password"
              type={showPassword ? 'text' : 'password'}
              required
              value={formData.password}
              onChange={handleChange}
              onFocus={() => setPasswordFocused(true)}
              onBlur={() => setPasswordFocused(false)}
              className={`w-full px-4 py-3 pr-11 rounded-xl border bg-white dark:bg-surface-dark text-gray-900 dark:text-white focus:ring-1 outline-none transition-all ${
                formData.password
                  ? allMet
                    ? 'border-emerald-400 focus:border-emerald-400 focus:ring-emerald-400'
                    : 'border-gray-300 dark:border-gray-700 focus:border-accent focus:ring-accent'
                  : 'border-gray-300 dark:border-gray-700 focus:border-accent focus:ring-accent'
              }`}
              placeholder="••••••••••••"
            />
            <button
              type="button"
              onClick={() => setShowPassword(v => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              tabIndex={-1}
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>

          {/* Strength bar */}
          {formData.password && (
            <div className="mt-2">
              <div className="flex gap-1 mb-1">
                {[1, 2, 3, 4].map(i => (
                  <div
                    key={i}
                    className={`h-1 flex-1 rounded-full transition-all duration-300 ${
                      i <= strength.score ? strength.color : 'bg-gray-200 dark:bg-gray-700'
                    }`}
                  />
                ))}
              </div>
              <p className={`text-xs font-medium ${
                strength.score <= 1 ? 'text-red-500' :
                strength.score === 2 ? 'text-yellow-500' :
                strength.score === 3 ? 'text-blue-500' : 'text-emerald-500'
              }`}>
                {strength.label} password
              </p>
            </div>
          )}

          {/* Requirements checklist - show when focused or has value */}
          {(passwordFocused || formData.password) && (
            <ul className="mt-3 space-y-1.5 p-3 bg-gray-50 dark:bg-gray-800/60 rounded-xl border border-gray-200 dark:border-gray-700">
              {REQUIREMENTS.map(req => {
                const met = req.test(formData.password)
                return (
                  <li key={req.id} className="flex items-center gap-2">
                    <span className={`flex-shrink-0 w-4 h-4 rounded-full flex items-center justify-center transition-all ${
                      met ? 'bg-emerald-500' : 'bg-gray-200 dark:bg-gray-600'
                    }`}>
                      {met
                        ? <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                        : <X className="w-2.5 h-2.5 text-gray-400 dark:text-gray-500" strokeWidth={3} />
                      }
                    </span>
                    <span className={`text-xs transition-colors ${
                      met ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-500 dark:text-gray-400'
                    }`}>
                      {req.label}
                    </span>
                  </li>
                )
              })}
            </ul>
          )}
        </div>

        {/* Confirm Password */}
        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            Confirm Password
          </label>
          <div className="relative">
            <input
              id="confirmPassword" name="confirmPassword"
              type={showConfirm ? 'text' : 'password'}
              required
              value={formData.confirmPassword}
              onChange={handleChange}
              className={`w-full px-4 py-3 pr-11 rounded-xl border bg-white dark:bg-surface-dark text-gray-900 dark:text-white focus:ring-1 outline-none transition-all ${
                formData.confirmPassword
                  ? passwordsMatch
                    ? 'border-emerald-400 focus:border-emerald-400 focus:ring-emerald-400'
                    : 'border-red-400 focus:border-red-400 focus:ring-red-400'
                  : 'border-gray-300 dark:border-gray-700 focus:border-accent focus:ring-accent'
              }`}
              placeholder="••••••••••••"
            />
            <button
              type="button"
              onClick={() => setShowConfirm(v => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              tabIndex={-1}
            >
              {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {formData.confirmPassword && (
            <p className={`mt-1.5 text-xs flex items-center gap-1.5 font-medium ${
              passwordsMatch ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500'
            }`}>
              {passwordsMatch
                ? <><Check className="w-3 h-3" strokeWidth={3} /> Passwords match</>
                : <><X className="w-3 h-3" strokeWidth={3} /> Passwords do not match</>
              }
            </p>
          )}
        </div>

        <div className="pt-1">
          <button
            type="submit"
            disabled={loading || !allMet || !passwordsMatch}
            className="w-full flex justify-center py-3.5 px-4 rounded-xl text-sm font-semibold text-white bg-black hover:bg-gray-800 dark:bg-white dark:text-black dark:hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black dark:focus:ring-white transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-white/30 dark:border-black/30 border-t-white dark:border-t-black rounded-full animate-spin" />
                Creating account…
              </span>
            ) : 'Create Account'}
          </button>
        </div>

        <p className="text-center text-xs text-muted-light dark:text-muted-dark">
          By signing up you agree to our{' '}
          <a href="/terms.html" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-700 dark:hover:text-gray-300">Terms</a>
          {' '}and{' '}
          <a href="/privacy.html" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-700 dark:hover:text-gray-300">Privacy Policy</a>.
        </p>
      </form>

      <div className="text-center">
        <p className="text-sm text-muted-light dark:text-muted-dark">
          Already have an account?{' '}
          <Link className="font-medium text-black dark:text-white hover:underline decoration-2 underline-offset-4" to="/login">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  )
}
